import { useQuery } from '@tanstack/react-query'
import { useSearch } from '@tanstack/react-router'

import { Button } from '@/components/button'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { Pager } from '@/components/pager'
import { notificationListQuery, useReadAll } from '@/features/notifications/api'
import { NotificationRow } from '@/features/notifications/row'
import type { System } from '@/lib/auth-store'
import { useT } from '@/lib/i18n/translate'
import { useClampedPage } from '@/lib/paging'
import { notificationsPathFor } from '@/lib/return-path'

/**
 * The notifications page (H3), one per host. Nothing is marked read by
 * opening it: a notice clears when clicked, or all at once by the button.
 */
export function NotificationsPage({ system }: { system: System }) {
  const t = useT()
  const { page } = useSearch({ strict: false }) as { page: number }
  const path = notificationsPathFor(system)
  const link = (page: number) => ({ to: path, search: { page } })
  const list = useQuery(notificationListQuery(system, page))
  useClampedPage(page, list.data, link)
  const readAll = useReadAll(system)
  const unread = list.data?.unread ?? 0

  return (
    <Page width="md" title={t('notification.title')} className="flex flex-col gap-4">
      {unread > 0 && (
        <Button variant="secondary" size="sm" loading={readAll.isPending} onClick={() => readAll.mutate()} className="self-end">
          {t('notification.markAllRead')}
        </Button>
      )}
      <FormError error={readAll.error} />
      {list.isPending && <p className="text-muted text-sm">{t('common.loading')}</p>}
      {list.error && (
        <div className="flex flex-col gap-2">
          <FormError error={list.error} />
          <Button variant="secondary" onClick={() => void list.refetch()} className="self-start">
            {t('common.retry')}
          </Button>
        </div>
      )}
      {list.data &&
        (list.data.items.length === 0 ? (
          <Card>
            <p className="text-muted text-sm">{t('notification.empty')}</p>
          </Card>
        ) : (
          <ul className="flex flex-col gap-2">
            {list.data.items.map((item) => (
              <li key={item.id}>
                <NotificationRow system={system} item={item} />
              </li>
            ))}
          </ul>
        ))}
      <Pager label={t('notification.pager')} page={page} total={list.data?.total ?? 0} link={link} />
    </Page>
  )
}

